<template>
  <div>
    <v-card id="summary-portfolio-performance-table" class="px-4 pt-2 pb-4 my-0 elevation-0">
      <div v-if="loadedData && type === 'pl'" class="mt-5">
        <v-chip-group v-model="plType" mandatory column>
          <v-chip
            value="plPlus"
            :color="plType === 'plPlus' ? DEFAULT_PALETTE.softGreen : undefined"
            small
            outlined
          >
            <span v-if="plMode === 'net'">
              {{ $t(`views.dashboardDetail.summary.portfolioPerformance.totalValuePlus`) }}
            </span>
            <span v-else-if="plMode === 'currency'">
              {{ $t(`views.dashboardDetail.summary.portfolioPerformance.plPlusBaseCurrency`) }}
              ({{ symbolBaseCurrency }})
            </span>
            <span v-else-if="plMode === 'percent'">
              {{
                $t(`views.dashboardDetail.summary.portfolioPerformance.plPlusBaseCurrencyPercent`)
              }}
            </span>
          </v-chip>
          <v-chip
            value="pl"
            :color="plType === 'pl' ? DEFAULT_PALETTE.primary : undefined"
            small
            outlined
          >
            <span v-if="plMode === 'net'">
              {{ $t(`views.dashboardDetail.summary.portfolioPerformance.totalValue`) }}
            </span>
            <span v-else-if="plMode === 'currency'">
              {{ $t(`views.dashboardDetail.summary.portfolioPerformance.plBaseCurrency`) }}
              ({{ symbolBaseCurrency }})
            </span>
            <span v-else-if="plMode === 'percent'">
              {{ $t(`views.dashboardDetail.summary.portfolioPerformance.plBaseCurrencyPercent`) }}
            </span>
          </v-chip>
          <v-chip
            v-if="plMode === 'net'"
            value="totalCost"
            :color="
              plType === 'totalCost'
                ? this.$vuetify.theme.dark
                  ? 'white'
                  : DEFAULT_PALETTE.secondary
                : undefined
            "
            small
            outlined
          >
            {{ $t(`views.dashboardDetail.summary.portfolioPerformance.totalCost`) }}
          </v-chip>
        </v-chip-group>
      </div>

      <v-data-table
        v-show="loadedData"
        id="summary-portfolio-performance-table-table"
        :headers="headers"
        :items="items"
        class="elevation-0 mt-3"
        mobile-breakpoint="0"
        :footer-props="{
          'items-per-page-options': [5, 10, 15, 20, 30, 50, -1],
        }"
        :items-per-page="15"
        :dense="user.preferences.denseTables"
      >
        <template v-slot:item="{ item, headers }">
          <tr>
            <td v-for="header in headers" :key="header.value">
              <template v-if="header.value === 'year'">
                <div class="year-column-value">
                  {{ item[header.value] }}
                </div>
              </template>
              <template v-else-if="type === 'pl'">
                <currency
                  v-if="plMode === 'percent' && item[header.value]"
                  :value="item[header.value]"
                  currency="%"
                />
                <currency
                  v-else-if="item[header.value]"
                  :value="item[header.value]"
                  :currency="user.baseCurrency"
                />
              </template>
              <template v-else>
                <TableNetPercent
                  v-if="item[header.value]"
                  :value="item[header.value]"
                  type="percent"
                />
              </template>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { get, sortBy } from 'lodash'
import { mapState } from 'vuex'
import { getCurrencySymbol } from '@/lib/utils/currencies.js'
import { DEFAULT_PALETTE, DARK_DEFAULT_PALETTE } from '@/lib/colors.js'
import tableMixin from '@/views/components/core/tableMixin.js'
import TableNetPercent from '@/views/components/common/TableNetPercent'
import Currency from '@/views/components/common/Currency.vue'

export default {
  components: {
    TableNetPercent,
    Currency,
  },
  mixins: [tableMixin],
  props: {
    loadedData: {
      type: Boolean,
      default: true,
    },
    byYear: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      required: true,
    },
    plMode: {
      type: String,
      default: 'net',
    },
    accumulated: {
      type: Boolean,
      default: false,
    },
    plus: {
      type: Boolean,
      default: true,
    },
    summary: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    defaultHeaders: [],
    items: [],
    plType: 'pl',
  }),
  computed: {
    ...mapState(['user']),
    DEFAULT_PALETTE: () => DEFAULT_PALETTE,
    DARK_DEFAULT_PALETTE: () => DARK_DEFAULT_PALETTE,
    symbolBaseCurrency() {
      return getCurrencySymbol(this.user.baseCurrency)
    },
  },
  watch: {
    async summary() {
      this.loadTableData()
    },
    async accumulated() {
      this.loadTableData()
    },
    async plus() {
      this.loadTableData()
    },
    async plMode() {
      this.loadTableData()
    },
    async plType() {
      this.loadTableData()
    },
    filters: {
      async handler() {
        await this.initPositions()
      },
      deep: true,
    },
  },
  methods: {
    get,
    loadTableData() {
      // change to pl if plType is totalCost
      if (this.type === 'pl' && this.plMode !== 'net' && this.plType === 'totalCost') {
        this.plType = 'pl'
      }

      let type
      if (this.type === 'pl') {
        if (this.plType === 'totalCost') type = 'totalCostWithComissionBaseCurrency'
        else {
          if (this.plMode === 'net')
            type =
              this.plType === 'plPlus' ? 'totalValueBaseCurrencyPlus' : 'totalValueBaseCurrency'
          else if (this.plMode === 'currency')
            type = this.plType === 'plPlus' ? 'plPlusBaseCurrency' : 'plBaseCurrency'
          else if (this.plMode === 'percent')
            type = this.plType === 'plPlus' ? 'plPlusBaseCurrencyPercent' : 'plBaseCurrencyPercent'
        }
      } else {
        if (this.accumulated) type = this.plus ? 'accumulatedYieldPlus' : 'accumulatedYield'
        else type = this.plus ? 'yieldPlus' : 'yield'
      }

      if (!this.byYear) {
        // bymonth

        this.defaultHeaders = [
          {
            text: '',
            value: 'year',
            divider: true,
          },
        ]
        for (let month = 1; month <= 12; month++) {
          this.defaultHeaders.push({
            text: this.$t(`common.months.${month}`).substring(0, 3).toUpperCase(),
            value: `${month}`,
            sortable: false,
          })
        }

        const result = this.summary.reduce((acc, item) => {
          const yearString = String(item.year)
          let yearGroup = acc.find((group) => group.year === yearString)
          if (!yearGroup) {
            yearGroup = { year: yearString }
            acc.push(yearGroup)
          }
          yearGroup[item.month] = item[type]
          return acc
        }, [])

        this.items = sortBy(result, 'year').reverse() // mayor a menor
      } else {
        const headers = []
        const result = {}

        this.summary.forEach((elem) => {
          const year = String(elem.year)
          headers.push({
            text: year,
            value: year,
            sortable: false,
          })
          result[year] = elem[type]
        })

        this.items = [result]
        this.defaultHeaders = headers
      }
    },
  },
}
</script>

<style lang="scss">
#summary-portfolio-performance-table {
  .year-column-value {
    font-weight: 500;
  }
}
</style>
